const DESKTOP_QUERY = '(min-width: 64rem)';

/**
 * Automatically applies sticky behavior to the shorter column
 * in the product information grid layout.
 */
class ProductInfoSticky extends HTMLElement {
  #resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.#measure()));
  #mediaQuery = window.matchMedia(DESKTOP_QUERY);

  connectedCallback() {
    if (this.#mediaQuery.matches) this.#measure();

    this.#mediaQuery.addEventListener('change', this.#onMediaChange);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mediaQuery.removeEventListener('change', this.#onMediaChange);
  }

  #measure() {
    if (!this.#mediaQuery.matches) return;

    // Query by class: the header prints before or after the gallery depending on
    // "show product header above gallery on mobile", so child position varies.
    const media = /** @type {HTMLElement | null} */ (this.querySelector(':scope > .product-information__media'));
    const head = /** @type {HTMLElement | null} */ (this.querySelector(':scope > .product-information__head'));
    const info = /** @type {HTMLElement | null} */ (this.querySelector(':scope > .product-information__info'));

    if (!media || !info) return;

    this.classList.remove('product-information--sticky-media', 'product-information--sticky-information');

    const columns = /** @type {HTMLElement[]} */ ([media, head, info].filter(Boolean));

    for (const column of columns) column.style.alignSelf = 'start';
    void this.offsetHeight;

    // The header is its own grid row above the info column; measure the two as one column.
    const mediaH = media.getBoundingClientRect().height;
    const infoH = info.getBoundingClientRect().bottom - (head ?? info).getBoundingClientRect().top;

    for (const column of columns) column.style.alignSelf = '';

    this.classList.add(
      mediaH <= infoH ? 'product-information--sticky-media' : 'product-information--sticky-information',
    );
  }

  /** @param {MediaQueryListEvent} event */
  #onMediaChange = (event) => {
    if (event.matches) {
      this.#measure();
    } else {
      this.classList.remove('product-information--sticky-media', 'product-information--sticky-information');
    }
  };
}

if (!customElements.get('product-info-sticky')) {
  customElements.define('product-info-sticky', ProductInfoSticky);
}
